import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  Sparkles, CheckCircle2, ArrowRight, ArrowLeft, Bot, LogOut,
  UserCheck, Building2, ShieldCheck
} from 'lucide-react';
import './Onboarding.css';

const OnboardingWizard = () => {
  const { user, employee, refreshOnboardingStatus, logout } = useAuth();
  const navigate = useNavigate();

  // Step identifiers:
  // 1: WELCOME
  // 2: HR_INTRO
  // 3: COMPANY_MCQS
  // 4: MCQ_DONE
  // 5: DEPT_INTRO
  // 6: DEPT_QUESTIONS
  // 7: SETUP_DONE
  const [step, setStep] = useState(1);

  // Data states
  const [hrProfile, setHrProfile] = useState(null);
  const [companyQuestions, setCompanyQuestions] = useState([]);
  const [deptQuestions, setDeptQuestions] = useState([]);
  
  // MCQ state
  const [mcqIndex, setMcqIndex] = useState(0);
  const [mcqAnswers, setMcqAnswers] = useState({});

  // Dept question state
  const [deptIndex, setDeptIndex] = useState(0);
  const [deptAnswers, setDeptAnswers] = useState({});

  const [loading, setLoading] = useState(true);

  const userName = user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : (user?.username || 'Team Member');
  const companyName = hrProfile?.company_name || 'Technaureus Info Solutions';
  const hrName = hrProfile?.name || 'Sarah Jenkins';
  const hrDesig = hrProfile?.designation || 'Chief HR Officer';
  const hrAvatar = hrProfile?.avatar_url || hrProfile?.avatar;

  const deptName = employee?.department_detail?.name || 'Assigned Department';
  const deptAvatar = employee?.department_detail?.avatar_image;

  useEffect(() => {
    initWizardData();
  }, []);

  const initWizardData = async () => {
    try {
      setLoading(true);

      // 1. Fetch HR Profile
      try {
        const hrRes = await api.get('/hr/active/');
        setHrProfile(hrRes.data);
      } catch (err) {
        console.error('HR fetch error:', err);
      }

      // 2. Fetch Predefined Company Questions
      let cQs = [];
      try {
        const mcqRes = await api.get('/onboarding/questions/');
        cQs = mcqRes.data.results || mcqRes.data || [];
        setCompanyQuestions(cQs);
      } catch (err) {
        console.error('Company questions fetch error:', err);
      }

      // 3. Fetch Department Questions
      let dQs = [];
      try {
        const deptRes = await api.get('/department-questions/');
        dQs = deptRes.data.results || deptRes.data || [];
        setDeptQuestions(dQs);
      } catch (err) {
        console.error('Dept questions fetch error:', err);
      }

      // 4. Fetch existing submissions to resume progress
      try {
        const meRes = await api.get('/auth/me/');
        const empId = meRes.data.employee?.id;
        if (empId) {
          const resultsRes = await api.get(`/employees/${empId}/onboarding-results/`);
          
          // Map existing company answers
          const mcqMap = {};
          (resultsRes.data.predefined_mcq_submissions || []).forEach(sub => {
            const matched = cQs.find(q => q.id === sub.question_id || q.question === sub.question);
            if (matched) mcqMap[matched.id] = sub.submitted_answer;
          });
          setMcqAnswers(mcqMap);

          // Map existing dept answers
          const deptMap = {};
          (resultsRes.data.department_submissions || []).forEach(sub => {
            const matched = dQs.find(q => q.id === sub.question_id || q.question === sub.question);
            if (matched) deptMap[matched.id] = sub.submitted_answer;
          });
          setDeptAnswers(deptMap);

          // Set starting indices for questions if resuming partially
          const firstUnansweredMcq = cQs.findIndex(q => !mcqMap[q.id]);
          const firstUnansweredDept = dQs.findIndex(q => !deptMap[q.id]);

          if (firstUnansweredMcq !== -1) {
            setMcqIndex(firstUnansweredMcq);
          }
          if (firstUnansweredDept !== -1) {
            setDeptIndex(firstUnansweredDept);
          }
        }
      } catch (subErr) {
        console.error('Submissions resume check error:', subErr);
      }

    } catch (err) {
      console.error('Wizard init error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMcqOption = async (qId, optionText) => {
    setMcqAnswers(prev => ({ ...prev, [qId]: optionText }));
    try {
      await api.post(`/onboarding/questions/${qId}/answer/`, { submitted_answer: optionText });
      await refreshOnboardingStatus();
    } catch (err) {
      console.error('Failed to submit MCQ answer:', err);
    }
  };

  const handleSelectDeptOption = async (qId, optionText) => {
    setDeptAnswers(prev => ({ ...prev, [qId]: optionText }));
    try {
      await api.post(`/department-questions/${qId}/answer/`, { submitted_answer: optionText });
      await refreshOnboardingStatus();
    } catch (err) {
      console.error('Failed to submit Dept answer:', err);
    }
  };

  const handleNextMcq = () => {
    if (mcqIndex < companyQuestions.length - 1) {
      setMcqIndex(prev => prev + 1);
    } else {
      setStep(4); // MCQ Completed Interstitial
    }
  };

  const handleNextDept = () => {
    if (deptIndex < deptQuestions.length - 1) {
      setDeptIndex(prev => prev + 1);
    } else {
      setStep(7); // Setup Completed Final
    }
  };

  const handleFinishWizard = async () => {
    await refreshOnboardingStatus();
    navigate('/dashboard', { replace: true });
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="onboarding-wizard-fullscreen-bg">
        <LoadingSpinner fullScreen label="Preparing your interactive onboarding journey..." />
      </div>
    );
  }

  return (
    <div className="onboarding-wizard-fullscreen-bg">
      
      {/* Top Header Bar for Wizard */}
      <header className="onboarding-wizard-topbar">
        <div className="onboarding-wizard-brand">
          <div className="onboarding-wizard-logo">
            <Sparkles size={20} />
          </div>
          <div>
            <span className="onboarding-wizard-title">Mentor<span style={{ color: '#4f46e5' }}>AI</span></span>
            <span className="onboarding-wizard-subtitle">Joinee Onboarding Setup</span>
          </div>
        </div>

        <div className="onboarding-wizard-top-right">
          <span className="onboarding-wizard-user-tag">
            <UserCheck size={14} /> {userName}
          </span>
          <button onClick={handleLogout} className="btn btn-secondary btn-sm" title="Sign Out">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      {/* Main Wizard Content Container */}
      <div className="onboarding-wizard-main-wrapper">

        {/* STEP 1: WELCOME SCREEN */}
        {step === 1 && (
          <div className="card onboarding-wizard-card animate-fade-in">
            <div className="onboarding-wizard-badge purple">
              <Sparkles size={14} /> Welcome to the Team
            </div>

            <h1 className="onboarding-wizard-heading">
              Welcome <span className="highlight-purple">{userName}</span> to the {companyName} family! 
            </h1>

            <p className="onboarding-wizard-desc">
              We are thrilled to have you join us! To help you get familiar with our culture, policies, and your assigned project technical stack, we have prepared a quick interactive onboarding journey.
            </p>

            <div className="onboarding-wizard-features-grid">
              <div className="wizard-feature-pill">
                <ShieldCheck size={18} className="icon-purple" />
                <span>Learn Official Company Guidelines</span>
              </div>
              <div className="wizard-feature-pill">
                <UserCheck size={18} className="icon-green" />
                <span>Meet HR & AI Office Buddy</span>
              </div>
              <div className="wizard-feature-pill">
                <Bot size={18} className="icon-blue" />
                <span>Connect with your Department AI Lead</span>
              </div>
            </div>

            <div className="onboarding-wizard-action-row center">
              <button className="btn btn-primary btn-lg wizard-btn" onClick={() => setStep(2)}>
                <span>Get Started / Next</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 2: HR INTRODUCTION BOX */}
        {step === 2 && (
          <div className="card onboarding-wizard-card animate-fade-in">
            <div className="onboarding-wizard-badge blue">
              <UserCheck size={14} /> Meet Your HR Office Buddy
            </div>

            <div className="onboarding-hr-intro-box">
              <div className="onboarding-hr-avatar-wrapper">
                {hrAvatar ? (
                  <img src={hrAvatar} alt={hrName} className="onboarding-hr-avatar-img" />
                ) : (
                  <div className="onboarding-hr-avatar-fallback">{hrName.charAt(0)}</div>
                )}
                <span className="online-indicator"></span>
              </div>

              <div className="onboarding-hr-details">
                <h3>{hrName}</h3>
                <span className="onboarding-hr-desig">{hrDesig} • {companyName}</span>
                
                <div className="onboarding-hr-speech-bubble">
                  <p>
                    "Hi <strong>{userName}</strong>! I'm <strong>{hrName}</strong>, your AI office buddy. I am here to assist you with company policies, leave rules, and workplace guidelines. You can ask me doubts about the company anytime!"
                  </p>
                </div>
              </div>
            </div>

            <div className="onboarding-wizard-action-row between">
              <button className="btn btn-secondary" onClick={() => setStep(1)}>
                <ArrowLeft size={16} /> Back
              </button>
              <button className="btn btn-primary wizard-btn" onClick={() => setStep(3)}>
                <span>Next → Begin Company Questions</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3: PREDEFINED COMPANY MCQS */}
        {step === 3 && (
          <div className="card onboarding-wizard-card animate-fade-in">
            {companyQuestions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <h3>No company MCQs configured yet.</h3>
                <button className="btn btn-primary" onClick={() => setStep(4)} style={{ marginTop: '16px' }}>
                  Continue to Next Step <ArrowRight size={16} />
                </button>
              </div>
            ) : (() => {
              const currentQ = companyQuestions[mcqIndex];
              const totalQ = companyQuestions.length;
              const selectedOpt = mcqAnswers[currentQ.id];

              const options = [
                { key: 'A', text: currentQ.option_a },
                { key: 'B', text: currentQ.option_b },
                { key: 'C', text: currentQ.option_c },
                { key: 'D', text: currentQ.option_d },
              ];

              return (
                <>
                  <div className="onboarding-wizard-step-header">
                    <div>
                      <span className="onboarding-wizard-step-tag">Company Culture & Policies</span>
                      <h2 className="onboarding-wizard-step-title">Question {mcqIndex + 1} of {totalQ}</h2>
                    </div>
                    <span className="badge badge-primary">Step 1 of 2 Assessments</span>
                  </div>

                  <div className="progress-bar-bg" style={{ marginBottom: '24px' }}>
                    <div className="progress-bar-fill" style={{ width: `${((mcqIndex + 1) / totalQ) * 100}%` }} />
                  </div>

                  <h3 className="onboarding-wizard-question-text">
                    {currentQ.question}
                  </h3>

                  <div className="onboarding-options-grid" style={{ marginBottom: '28px' }}>
                    {options.map((opt) => {
                      const checked = selectedOpt === opt.text || selectedOpt === opt.key;
                      return (
                        <div
                          key={opt.key}
                          onClick={() => handleSelectMcqOption(currentQ.id, opt.text)}
                          className={`onboarding-option-btn ${checked ? 'selected' : ''}`}
                        >
                          <div style={{
                            width: '24px', height: '24px', borderRadius: '50%',
                            border: checked ? '6px solid #6366f1' : '2px solid rgba(255, 255, 255, 0.3)',
                            background: checked ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                            flexShrink: 0,
                            transition: 'all 0.2s ease'
                          }} />
                          <span className="onboarding-option-label" style={{ fontWeight: checked ? 600 : 400, color: checked ? '#ffffff' : '#f8fafc' }}>
                            {opt.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="onboarding-wizard-action-row between">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setMcqIndex(prev => Math.max(0, prev - 1))}
                      disabled={mcqIndex === 0}
                    >
                      <ArrowLeft size={16} /> Previous
                    </button>

                    <button
                      className="btn btn-primary wizard-btn"
                      onClick={handleNextMcq}
                      disabled={!selectedOpt}
                    >
                      <span>{mcqIndex < totalQ - 1 ? 'Next Question' : 'Complete MCQs'}</span>
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* STEP 4: COMPANY MCQS COMPLETED INTERSTITIAL */}
        {step === 4 && (
          <div className="card onboarding-wizard-card animate-fade-in text-center">
            <div className="onboarding-success-icon-box">
              <CheckCircle2 size={48} style={{ color: '#059669' }} />
            </div>

            <h1 className="onboarding-wizard-heading" style={{ marginTop: '16px', textAlign: 'center' }}>
              Congratulations <span className="highlight-purple">{userName}</span>! 
            </h1>

            <p className="onboarding-wizard-desc" style={{ maxWidth: '520px', margin: '12px auto 28px', textAlign: 'center' }}>
              Your general company onboarding assessment is successfully completed! Next, let's introduce you to your Department AI Team Lead.
            </p>

            <div className="onboarding-wizard-action-row center">
              <button className="btn btn-primary btn-lg wizard-btn" onClick={() => setStep(5)}>
                <span>Next → Meet Your AI Team Lead</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 5: TEAM LEAD / DEPT HEAD INTRO BOX */}
        {step === 5 && (
          <div className="card onboarding-wizard-card animate-fade-in">
            <div className="onboarding-wizard-badge green">
              <Building2 size={14} /> Meet Your Department Lead
            </div>

            <div className="onboarding-hr-intro-box dept-box">
              <div className="onboarding-hr-avatar-wrapper green-border">
                {deptAvatar ? (
                  <img src={deptAvatar} alt={deptName} className="onboarding-hr-avatar-img" />
                ) : (
                  <div className="onboarding-hr-avatar-fallback green-bg">
                    <Bot size={36} />
                  </div>
                )}
                <span className="online-indicator green"></span>
              </div>

              <div className="onboarding-hr-details">
                <h3>{deptName} AI Lead</h3>
                <span className="onboarding-hr-desig">{deptName} Department Technical Mentor</span>
                
                <div className="onboarding-hr-speech-bubble green-bubble">
                  <p>
                    "Hi <strong>{userName}</strong>! I'm your Team Lead & AI Mentor for <strong>{deptName}</strong>. I am here to assist you about your assigned projects at any time. I'm here for you!"
                  </p>
                </div>
              </div>
            </div>

            <div className="onboarding-wizard-action-row between">
              <button className="btn btn-secondary" onClick={() => setStep(4)}>
                <ArrowLeft size={16} /> Back
              </button>

              <button
                className="btn btn-primary wizard-btn"
                style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}
                onClick={() => {
                  if (deptQuestions.length === 0) {
                    setStep(7); // Skip if no dept questions
                  } else {
                    setStep(6);
                  }
                }}
              >
                <span>Next → Start Department Assessment</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}

        {/* STEP 6: DEPARTMENT WISE QUESTIONS */}
        {step === 6 && (
          <div className="card onboarding-wizard-card animate-fade-in">
            {deptQuestions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <h3>No department questions for {deptName} yet.</h3>
                <button className="btn btn-primary" onClick={() => setStep(7)} style={{ marginTop: '16px' }}>
                  Proceed to Final Setup <ArrowRight size={16} />
                </button>
              </div>
            ) : (() => {
              const currentQ = deptQuestions[deptIndex];
              const totalQ = deptQuestions.length;
              const selectedOpt = deptAnswers[currentQ.id];

              const options = [
                { key: 'A', text: currentQ.option_a },
                { key: 'B', text: currentQ.option_b },
                { key: 'C', text: currentQ.option_c },
                { key: 'D', text: currentQ.option_d },
              ];

              return (
                <>
                  <div className="onboarding-wizard-step-header">
                    <div>
                      <span className="onboarding-wizard-step-tag green">{deptName} Domain</span>
                      <h2 className="onboarding-wizard-step-title">Question {deptIndex + 1} of {totalQ}</h2>
                    </div>
                    <span className="badge badge-success">Step 2 of 2 Assessments</span>
                  </div>

                  <div className="progress-bar-bg" style={{ marginBottom: '24px' }}>
                    <div className="progress-bar-fill" style={{ width: `${((deptIndex + 1) / totalQ) * 100}%`, background: 'linear-gradient(90deg, #059669, #10b981)' }} />
                  </div>

                  <h3 className="onboarding-wizard-question-text">
                    {currentQ.question}
                  </h3>

                  <div className="onboarding-options-grid" style={{ marginBottom: '28px' }}>
                    {options.map((opt) => {
                      const checked = selectedOpt === opt.text || selectedOpt === opt.key;
                      return (
                        <div
                          key={opt.key}
                          onClick={() => handleSelectDeptOption(currentQ.id, opt.text)}
                          className={`onboarding-option-btn dept-option ${checked ? 'dept-selected' : ''}`}
                        >
                          <div style={{
                            width: '24px', height: '24px', borderRadius: '50%',
                            border: checked ? '6px solid #10b981' : '2px solid rgba(255, 255, 255, 0.3)',
                            background: checked ? '#ffffff' : 'rgba(255, 255, 255, 0.05)',
                            flexShrink: 0,
                            transition: 'all 0.2s ease'
                          }} />
                          <span className="onboarding-option-label" style={{ fontWeight: checked ? 600 : 400, color: checked ? '#ffffff' : '#f8fafc' }}>
                            {opt.text}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="onboarding-wizard-action-row between">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setDeptIndex(prev => Math.max(0, prev - 1))}
                      disabled={deptIndex === 0}
                    >
                      <ArrowLeft size={16} /> Previous
                    </button>

                    <button
                      className="btn btn-primary wizard-btn"
                      style={{ background: 'linear-gradient(135deg, #059669, #047857)' }}
                      onClick={handleNextDept}
                      disabled={!selectedOpt}
                    >
                      <span>{deptIndex < totalQ - 1 ? 'Next Question' : 'Complete Setup'}</span>
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </>
              );
            })()}
          </div>
        )}

        {/* STEP 7: SETUP COMPLETED FINAL SCREEN */}
        {step === 7 && (
          <div className="card onboarding-wizard-card animate-fade-in text-center">
            <div className="onboarding-success-icon-box giant">
              <Sparkles size={56} style={{ color: '#4f46e5' }} />
            </div>

            <h1 className="onboarding-wizard-heading" style={{ marginTop: '20px', textAlign: 'center' }}>
              Congratulations <span className="highlight-purple">{userName}</span>! 🚀
            </h1>

            <p className="onboarding-wizard-desc" style={{ maxWidth: '560px', margin: '12px auto 28px', textAlign: 'center' }}>
              Your onboarding setup is successfully completed! You are now fully unlocked to explore your personalized dashboard, company policies, and AI project mentors.
            </p>

            <div className="onboarding-wizard-action-row center">
              <button
                className="btn btn-primary btn-lg wizard-btn"
                style={{ padding: '16px 32px', fontSize: '1.1rem' }}
                onClick={handleFinishWizard}
              >
                <span>Finish & Enter Workspace</span>
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default OnboardingWizard;
